import { PRETTY_GEOM_TYPES } from '@/constants/filterCriteria/location';
import { useAgcGeoRoles, useUserDetails } from '@/hooks';
import type { SearchTemplateData } from '@/state/searchTemplate/searchTemplateData';
import {
  TemplateValidationError,
  templateValidationErrorsAtom,
} from '@/state/searchTemplate/templateValidation';
import { AgcGeoRoles } from '@/types';
import {
  AgDialog,
  DOMAIN_CODE_LABELS,
  DomainCode,
  domainCodeAtom,
  useSaladBar,
} from '@australiangreens/ag-internal-components';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Button, Typography } from '@mui/material';
import { useAtomValue } from 'jotai';
import { JSX } from 'react';
import { useNavigate } from 'react-router';

type TemplateValidationWarningModalProps = {
  searchTemplateData: SearchTemplateData;
  onProceed: () => void;
};

export function formatAccessRoles(
  domainCode: DomainCode,
  rolesByDomain: Partial<Record<DomainCode, { roleId: string; roleName: string }[]>> | undefined
): [string, JSX.Element] {
  if (!rolesByDomain || !(domainCode in rolesByDomain))
    return [
      'None\n',
      <>
        <strong>None</strong>
        <br />
      </>,
    ];

  const entries: string[] = [];
  for (const [domain, roles] of Object.entries(rolesByDomain)) {
    if (domain === domainCode) {
      for (const role of roles ?? []) {
        entries.push(`${role.roleName}`);
      }
    }
  }
  const stringFormat = entries.length > 0 ? `${entries.join(', ')}\n` : 'None\n';
  const jsxFormat =
    entries.length > 0 ? (
      <>
        <strong>{entries[0]}</strong>
        {entries.slice(1).map((entry) => (
          <>
            , <strong>{entry}</strong>
          </>
        ))}
        <br />
      </>
    ) : (
      <>
        <strong>None</strong>
        <br />
      </>
    );

  return [stringFormat, jsxFormat];
}

export function formatGeoRoles(agcGeoRoles: AgcGeoRoles[] | undefined): [string, JSX.Element] {
  if (!agcGeoRoles || !agcGeoRoles.length) return ['', <></>];

  const entries: string[] = ['AgcGeoRoles:'];
  for (const agcGeoRole of agcGeoRoles) {
    entries.push(
      `    ${agcGeoRole.role} - ${agcGeoRole.label} (${PRETTY_GEOM_TYPES[agcGeoRole.geometryType]}) [GeometryID = ${agcGeoRole.geometryId}]`
    );
  }
  const entriesAsHTML = (
    <>
      AgcGeoRoles:
      <br />
      {agcGeoRoles.map((agcGeoRole) => (
        <>
          <code>{'    '}</code>
          <strong>{agcGeoRole.role}</strong> - <strong>{agcGeoRole.label}</strong> (
          <strong>{PRETTY_GEOM_TYPES[agcGeoRole.geometryType]}</strong>) [GeometryID ={' '}
          <strong>{agcGeoRole.geometryId}</strong>]<br />
        </>
      ))}
    </>
  );

  return [entries.join('\n') + '\n', entriesAsHTML];
}

export function buildDetailsText(
  searchTemplateData: SearchTemplateData,
  templateValidationErrors: TemplateValidationError[],
  domainCode: DomainCode,
  userName: string,
  contactId: number,
  rolesByDomain: Partial<Record<DomainCode, { roleId: string; roleName: string }[]>> | undefined,
  agcGeoRoles: AgcGeoRoles[] | undefined
): [string, JSX.Element] {
  const orgLabel = DOMAIN_CODE_LABELS[domainCode] || domainCode.toUpperCase();
  const valuesSectionInitial = templateValidationErrors.map(
    (e) => `    ${e.filterName}: ${e.fieldName}`
  );
  const valueSectionSet = new Set(valuesSectionInitial);
  const valuesSectionArray = [...valueSectionSet].sort();
  const valuesSection = valuesSectionArray.join('\n');
  const ourAccessRoles = formatAccessRoles(domainCode, rolesByDomain);
  const ourGeoRoles = formatGeoRoles(agcGeoRoles);
  const templateName = searchTemplateData.templateName ?? '';
  const templateId = searchTemplateData.templateId ?? 0;

  const stringFormat = [
    `Template: ${templateName} - [ID = ${templateId}]`,
    `Values:`,
    valuesSection,
    `Org: ${orgLabel}`,
    `User: ${userName} - [ContactID = ${contactId}]`,
    `Access: ${ourAccessRoles[0]}${ourGeoRoles[0]}`,
  ].join('\n');

  const jsxFormat = (
    <>
      Template: <strong>{templateName}</strong> - [ID = <strong>{templateId}</strong>]<br />
      Values:
      <br />
      {valuesSectionArray.map((x) => (
        <>
          <strong>{x}</strong>
          <br />
        </>
      ))}
      Org: <strong>{orgLabel}</strong>
      <br />
      User: <strong>{userName}</strong> - [ContactID = <strong>{contactId}</strong>]<br />
      Access: {ourAccessRoles[1]}
      {ourGeoRoles[1]}
    </>
  );

  return [stringFormat, jsxFormat];
}

export default function TemplateValidationWarningModal({
  searchTemplateData,
  onProceed,
}: TemplateValidationWarningModalProps) {
  const navigate = useNavigate();
  const { user } = useAuth0();
  const { data: userDetails } = useUserDetails();
  const { data: agcGeoRoles } = useAgcGeoRoles();
  const domainCode = useAtomValue(domainCodeAtom);
  const templateValidationErrors = useAtomValue(templateValidationErrorsAtom);
  const { enqueueSuccessNotification } = useSaladBar();

  const userName = user?.name ?? 'Unknown';
  const contactId = userDetails?.contactId ?? -1;

  const detailsText = buildDetailsText(
    searchTemplateData,
    templateValidationErrors,
    domainCode,
    userName,
    contactId,
    userDetails?.rolesByDomain,
    agcGeoRoles
  );

  const clipboardText = [
    'This search template was created using values that no longer exist or are unavailable to the user.',
    '',
    detailsText[0],
  ].join('\n');

  const handleCopyToClipboard = async () => {
    await navigator.clipboard.writeText(clipboardText);
    enqueueSuccessNotification('Copied to clipboard');
  };

  const handleCancel = () => {
    navigate('/searchlibrary');
  };

  // Note: isOpen={userDetails !== undefined && agcGeoRoles !== undefined} as it
  // is best to show the dialog when both pieces of data are present. It does
  // not look good for the user when showing the dialog, loading the second bit of
  // data, and then expanding the dialog on the screen. It looks jumpy.

  return (
    <AgDialog
      dialogTitle="Warning!"
      isOpen={userDetails !== undefined && agcGeoRoles !== undefined}
      onClose={handleCancel}
      disableCloseOnBackdropOrEscape={true}
      primaryButton={{
        text: 'Proceed',
        onClick: async () => onProceed(),
      }}
      secondaryButton={{
        text: 'Cancel',
        onClick: async () => handleCancel(),
      }}
      data-testid="template-validation-warning-modal"
      maxWidth={'md'}
    >
      <Typography variant="body1" gutterBottom>
        This search template was created using values that no longer exist or are unavailable to
        you. Please report this to your state/territory office or helpdesk, with the following
        details:
      </Typography>

      <Box
        sx={{
          backgroundColor: 'grey.100',
          padding: 2,
          borderRadius: 1,
          fontFamily: 'monospace',
          fontSize: '0.85rem',
          whiteSpace: 'pre-wrap',
          marginY: 2,
        }}
        data-testid="template-validation-details"
      >
        {detailsText[1]}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: 2
        }}>
        <Button onClick={handleCopyToClipboard}>Copy to clipboard</Button>
      </Box>

      <Typography variant="body1">
        If you proceed, filters with missing value(s) will be highlighted. Click &quot;Cancel&quot;
        to be taken back to the Search Templates menu.
      </Typography>
    </AgDialog>
  );
}
